import { useEffect, useRef, useState } from 'react';
import {
  Code2,
  FileText,
  Image as ImageIcon,
  Table as TableIcon,
  type LucideIcon,
} from 'lucide-react';

import type { AddableContentType } from './editorTypes';
import styles from './AddContentPicker.module.css';

interface AddContentPickerProps {
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
  label?: string;
  onAdd: (type: AddableContentType) => void;
}

export const CONTENT_TYPE_OPTIONS: Array<{
  Icon: LucideIcon;
  label: string;
  type: AddableContentType;
}> = [
  {
    Icon: FileText,
    label: 'Text block',
    type: 'TEXT',
  },
  {
    Icon: ImageIcon,
    label: 'Image',
    type: 'IMAGE',
  },
  {
    Icon: TableIcon,
    label: 'Table',
    type: 'TABLE',
  },
  {
    Icon: Code2,
    label: 'Iframe',
    type: 'IFRAME',
  },
];

export function AddContentPicker({
  ariaLabel,
  className,
  disabled = false,
  label = 'Add content',
  onAdd,
}: AddContentPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div
      className={[styles.root, className].filter(Boolean).join(' ')}
      ref={rootRef}
    >
      <button
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        className={styles.trigger}
        disabled={disabled}
        onClick={() => {
          if (disabled) {
            return;
          }

          setIsOpen((currentValue) => !currentValue);
        }}
        type="button"
      >
        {label}
      </button>

      {isOpen ? (
        <div aria-label="Add content types" className={styles.menu} role="menu">
          {CONTENT_TYPE_OPTIONS.map((option) => (
            <button
              className={styles.option}
              key={option.type}
              onClick={() => {
                onAdd(option.type);
                setIsOpen(false);
              }}
              role="menuitem"
              type="button"
            >
              <option.Icon aria-hidden="true" size={16} strokeWidth={2} />
              <span className={styles.optionLabel}>{option.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
